import WaysHighlightComponent from "ags/components/ways-highlight-component";
import createGreenScreenFilter from "ags/filters/green-screen-filter";
import lineColours from "ags/utils/line-colours";
import { BevelFilter, OutlineFilter } from "pixi-filters";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import slotDefinition from "slotworks/model/slot-definition";

export default class LinesHighlightComponent extends WaysHighlightComponent {

    public static LINE_THICKNESS = 6;

    protected createSymbolOutline(symbols: SymbolComponent[], win: LineResult) {
        const matrixLayer = symbols[0].getMatrixLayer();
        const matrixBounds = matrixLayer.bounds;
        const waysHighlightTexture = this.createOutlineTexture(symbols, win);
        const waysHighlightSprite = this.createSprite(waysHighlightTexture);

        waysHighlightSprite.filters = this.getFilters(win.line);

        const line = this.drawLine(win.line);
        line.filters = [this.getBevelFilter(win.line)];

        const lineHighlightTexture = this.createRenderTexture(matrixBounds);

        canvasManager.renderer.render(line, lineHighlightTexture, false);
        canvasManager.renderer.render(waysHighlightSprite, lineHighlightTexture, false);

        const lineHighlightSprite = this.createSprite(lineHighlightTexture);

        lineHighlightSprite.filters = [createGreenScreenFilter()];

        return lineHighlightSprite;
    }

    protected getFilters(lineNumber = 1) {
        const colour = lineColours[lineNumber];
        const outlineFilter = new OutlineFilter(LinesHighlightComponent.LINE_THICKNESS, colour, this.alpha);
        const bevelFilter = this.getBevelFilter(lineNumber);

        return [outlineFilter, bevelFilter];
    }

    protected getBevelFilter(lineNumber: number) {

        const colour = lineColours[lineNumber];

        return new BevelFilter({
            rotation: 54,
            thickness: Math.round(LinesHighlightComponent.LINE_THICKNESS / 2),
            lightColor: this.shadeColor(colour, 20),
            lightAlpha: 0.7,
            shadowColor: this.shadeColor(colour, -20),
            shadowAlpha: 0.7
        });
    }

    protected drawLine(lineIndex: number) {

        const lineDefinition = slotDefinition.lines.values[lineIndex];

        const graphics = new PIXI.Graphics();
        graphics.lineStyle(LinesHighlightComponent.LINE_THICKNESS + 1, lineColours[lineIndex], this.alpha);

        const firstPosition = lineDefinition[0];
        const firstSymbolPosition = Layers.MatrixContent.getPosition("symbol_" + (firstPosition.x + 1) + "_" + (firstPosition.y + 1));
        graphics.moveTo(firstSymbolPosition.x, firstSymbolPosition.y + firstSymbolPosition.height * 0.5);

        for (const point of lineDefinition) {
            const x = point.x + 1;
            const y = point.y + 1;

            const symbolPosition = Layers.MatrixContent.getPosition(`symbol_${x}_${y}`);

            graphics.lineTo(symbolPosition.x + symbolPosition.width * 0.5, symbolPosition.y + symbolPosition.height * 0.5);
        }

        const lastPosition = lineDefinition[lineDefinition.length - 1];
        const lastSymbolPosition = Layers.MatrixContent.getPosition("symbol_" + (lastPosition.x + 1) + "_" + (lastPosition.y + 1));
        graphics.lineTo(lastSymbolPosition.x + lastSymbolPosition.width, lastSymbolPosition.y + lastSymbolPosition.height * 0.5);

        graphics.x += this.padding / 2;
        graphics.y += this.padding / 2;

        return graphics;
    }

    /**
     * https://gist.github.com/renancouto/4675192
     *
     * @param color
     * @param percent
     */
    protected shadeColor(color: number, percent: number): number {

        const amount = Math.round(2.55 * percent);
        const red = (color >> 16) + amount;
        const blue = (color >> 8 & 0x00FF) + amount;
        const green = (color & 0x0000FF) + amount;

        return 0x1000000 + (red < 255 ? red < 1 ? 0 : red : 255) * 0x10000 + (blue < 255 ? blue < 1 ? 0 : blue : 255) * 0x100 + (green < 255 ? green < 1 ? 0 : green : 255);
    }
}
